import { StreamConcurrencyConfiguration } from '../StreamConcurrencyConfiguration';
import { StreamConcurrencyService } from '../StreamConcurrencyService';
import { createLogger, Error, ErrorCodes, Logger, LoggerLevel, PlatformUtil } from '@quickplay/foundation';
import { StreamConcurrencyManager } from '../StreamConcurrencyManager';
import { PLAYBACK_STATES, Player } from '@quickplay/player-interface';
import { PlatformAuthorizer } from '@quickplay/platform-authorizer';
import { createStreamConcurrencyServiceImpl } from '../service/StreamConcurrencyServiceImpl';

const TAG_NAME = 'FL_STREAM_CONCURRENCY';

export function createStreamConcurrencyManager(
  deviceId: string,
  streamConcurrencyConfiguration: StreamConcurrencyConfiguration,
  platformAuthorizer: PlatformAuthorizer,
  streamConcurrencyService: StreamConcurrencyService = createStreamConcurrencyServiceImpl(
    streamConcurrencyConfiguration.endPointUrl,
    platformAuthorizer,
  ),
  heartbeatToken: string | undefined = undefined,
): StreamConcurrencyManager {
  let interval: any;
  let numberOfStreamConcurrencyFailures = 0;

  const logger: Logger = createLogger(LoggerLevel.OFF, TAG_NAME);
  const streamConcurrencySyncIntervalInMS = streamConcurrencyConfiguration.syncIntervalMS || 3000;
  const maxAllowedFailures = streamConcurrencyConfiguration.maxAllowedFailures || 2;
  const internalStreamConcurrencyService = streamConcurrencyService;
  let isStreamDeleted = false;

  const streamConcurrencyManager = {
    get streamConcurrencyService(): StreamConcurrencyService {
      return internalStreamConcurrencyService;
    },

    get deviceId(): string {
      return deviceId;
    },

    processPlayerStateChange(player: Player): void {
      switch (player.playerState) {
        case PLAYBACK_STATES.STARTED: {
          startTimer(player);
          isStreamDeleted = false;
          break;
        }
        case PLAYBACK_STATES.PAUSED:
        case PLAYBACK_STATES.IDLE: {
          if (!isStreamDeleted) {
            deleteStream();
            isStreamDeleted = true;
          }
          stopTimer();
          break;
        }
        default:
          break;
      }
    },
  };

  /* Private functions */
  function putStream(player: Player): void {
    internalStreamConcurrencyService.putStreamForDeviceId(deviceId, heartbeatToken).catch((error: Error) => {
      // Note: FLFoundation's network stack seems to think fetch calls are timing out
      // while in reality the calls are actually successful. This is observed on quiet a
      // sizable number of PS4 devices and only in certain cases (not reproducible in FL sample app).
      // Since this is affecting a lot of users, we are ignoring timeout calls only on PS4 platform.
      // See FLPS-33 for more details
      if (!isNetworkFailureOnPS4(error)) {
        numberOfStreamConcurrencyFailures++;
      } else {
        logger.warn('Ignoring stream concurrency put stream ping time-out on PlayStation 4 platform');
      }

      const isServerError = error && error.doesBelongToCategory(ErrorCodes.ERROR_CATEGORY_MASK_SERVER);
      if (isServerError || numberOfStreamConcurrencyFailures >= maxAllowedFailures) {
        player.abort(error);
        stopTimer();
      }
    });
  }

  function deleteStream(): void {
    internalStreamConcurrencyService.deleteStreamForDeviceId(deviceId).catch((_error: Error) => {
      // do nothing
    });
  }

  function startTimer(player: Player): void {
    if (interval) return;
    interval = setInterval(() => {
      putStream(player);
    }, streamConcurrencySyncIntervalInMS);
  }

  function stopTimer(): void {
    clearInterval(interval);
    interval = undefined;
  }

  function isNetworkFailureOnPS4(error: Error): boolean {
    const isNetworkError = error && error.doesBelongToCategory(ErrorCodes.ERROR_CATEGORY_MASK_NETWORK);
    return PlatformUtil.isPS4() && isNetworkError;
  }

  return streamConcurrencyManager;
}
